import { useCallback, useEffect, useRef, useState } from 'react';
import { bridgeApi, menuApi } from '@verlo/shared/services/api';
import { Button } from '@verlo/shared/components/ui/button';
import { Notice } from '@verlo/shared/components/ui/notice';
import { Signal } from '@verlo/shared/components/ui/signal';
import { Skeleton } from '@verlo/shared/components/ui/skeleton';
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue,
} from '@verlo/shared/components/ui/select';
import { FileSpreadsheet, Upload, Check, Minus } from 'lucide-react';
import { toast } from 'sonner';
import { usePageTitle } from '../../components/Layout';

/**
 * Bringing in the takings a venue made on somebody else's till (Verlo Bridge).
 *
 * This is the screen the back-office product is sold on. A venue three years into an EPOS
 * contract hands us the export their till already produces, and it becomes their reporting
 * — the switch to Verlo's floor comes at renewal, by which point their numbers are here.
 *
 * It differs from the menu importer in one way that shapes the whole page. A menu file is
 * either right or wrong. A sales file is *partial*: a transaction export makes every report
 * work, a product-mix report makes revenue and GP work and can never make covers mean
 * anything, because that was never in the file. So the middle of this screen is not a row
 * preview — it is `capability`, stated plainly before anything is committed rather than
 * discovered in an empty chart a fortnight later. That is the whole reason this page reads
 * differently from every other importer in the back office.
 *
 * Two things the backend decided that this screen has to honour:
 *
 * 1. **Money is the measure, not count.** Twenty matched products out of four hundred can
 *    still be ninety per cent of takings, so `revenue_mapped_pct` is the number on screen
 *    and the biggest sellers are at the top.
 * 2. **Recognised and confirmed are different facts.** A PLU of theirs that matches one of
 *    ours is a guess worth making; a person agreeing with it is a decision worth keeping.
 *    The row says which it is rather than flattening both into a tick.
 */

const gbp = (n) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' })
  .format(Number(n) || 0);

//: Quantities arrive summed as floats, so a round number arrives as 12.0. Nobody sold 12.0
//: of anything.
const qty = (n) => new Intl.NumberFormat('en-GB', { maximumFractionDigits: 1 }).format(Number(n) || 0);

//: A trading day as the venue says it: "14 Sep", not "2026-09-14".
const day = (iso) => new Date(`${iso}T12:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

const NONE = '__none__';

const SalesImportPage = () => {
  usePageTitle('Import sales');   // the top line has the title: no second one below
  const fileRef = useRef(null);
  const [record, setRecord] = useState(null);
  const [mapping, setMapping] = useState(null);
  const [capability, setCapability] = useState(null);
  const [menu, setMenu] = useState([]);
  const [all, setAll] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  // The dropdown offers the whole menu, not only the suggestions. `get_sales_mapping` sends
  // at most three, and only those scoring 0.3 or better — on a four-hundred-product export
  // plenty of dishes get none, and a screen that can only accept a suggestion cannot map
  // them at all.
  useEffect(() => {
    menuApi.getAll().then(({ data }) => setMenu(data || [])).catch(() => setMenu([]));
  }, []);

  /** Mapping and capability together: what a file can report depends on how much of its
   *  money is matched, so neither is ever read without the other. */
  const load = useCallback(async (id) => {
    const [m, c] = await Promise.all([bridgeApi.getSalesMapping(id), bridgeApi.getCapability(id)]);
    setMapping(m.data);
    setCapability(c.data);
  }, []);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    setDone(null);
    setAll(false);
    try {
      const res = await bridgeApi.upload('sales', file);
      setRecord(res.data);
      await load(res.data.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "That file couldn't be read");
      setRecord(null);
      setMapping(null);
      setCapability(null);
    } finally {
      setBusy(false);
    }
  };

  /** One decision, remembered for the org — so the second upload of the same export needs
   *  no sitting at all.
   *
   *  Applied on screen first and saved behind it. Re-reading the whole mapping after every
   *  dropdown would score four hundred products against the menu again for one changed row,
   *  and mapping a file is forty of these in a row. The percentage is arithmetic over
   *  numbers already on screen; `capability` is refreshed from the server afterwards,
   *  because whether the GP report works is the backend's rule to state, not ours. */
  const decide = async (sourceKey, menuItemId) => {
    const next = {
      ...mapping,
      products: mapping.products.map((p) => (
        p.key === sourceKey ? { ...p, menu_item_id: menuItemId, remembered: true } : p
      )),
    };
    const revenue = next.products.reduce((t, p) => t + (p.revenue || 0), 0) || 1;
    next.mapped = next.products.filter((p) => p.menu_item_id).length;
    next.revenue_mapped_pct = Math.round(
      1000 * next.products.filter((p) => p.menu_item_id).reduce((t, p) => t + (p.revenue || 0), 0) / revenue,
    ) / 10;
    setMapping(next);

    try {
      await bridgeApi.saveSalesMapping(record.id, [{ source_key: sourceKey, menu_item_id: menuItemId }]);
      const { data } = await bridgeApi.getCapability(record.id);
      setCapability(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save that match");
      await load(record.id);          // put the screen back to what the server actually has
    }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const res = await bridgeApi.commitToSales(record.id, {});
      setDone(res.data);
      toast.success(`${res.data.days} ${res.data.days === 1 ? 'day' : 'days'} of trading imported`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't import those sales", { duration: 8000 });
    } finally {
      setBusy(false);
    }
  };

  const products = mapping?.products || [];
  const shown = all ? products : products.slice(0, 50);

  return (
    <div className="space-y-6" data-testid="sales-import-page">
      <div className="space-y-1">
        <p className="max-w-2xl text-[14px] text-ink-soft">
          The export your current till already produces: a transaction file, or a
          product-mix report. We&rsquo;ll match the dishes to your menu and tell you which
          reports it can answer before anything is imported.
        </p>
      </div>

      {/* Upload */}
      <div className="rounded-lg border border-hairline bg-surface p-5">
        <input
          id="sales-import-file"
          ref={fileRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          className="sr-only"
          onChange={(e) => upload(e.target.files?.[0])}
        />
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="sales-import-choose">
            <Upload size={15} className="mr-1.5" />
            {record ? 'Choose a different file' : 'Choose a file'}
          </Button>
          <span className="text-[13px] text-ink-faint">
            {record
              ? <><FileSpreadsheet size={13} className="mr-1 inline" />{record.filename || 'spreadsheet'} &middot; {record.row_count} rows</>
              : 'CSV or Excel'}
          </span>
        </div>
      </div>

      {busy && !record && <Skeleton className="h-40 w-full" />}

      {capability && (
        <div className="rounded-lg border border-hairline bg-surface p-5 space-y-4" data-testid="sales-import-capability">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="font-heading text-[15px] font-bold text-ink">What this file can tell you</h2>
            <span className="font-mono text-[12.5px] tabular-nums text-ink-faint">
              {mapping?.revenue_mapped_pct ?? 0}% of takings matched
            </span>
          </div>
          <ul className="space-y-1.5">
            {(capability.reports || []).map((r) => (
              <li key={r.key} className="flex items-start gap-2.5 text-[13.5px]" data-testid={`sales-capability-${r.key}`}>
                {r.works
                  ? <Check size={15} className="mt-[3px] shrink-0 text-signal-ok" />
                  : <Minus size={15} className="mt-[3px] shrink-0 text-ink-faint" />}
                <span className={r.works ? 'text-ink' : 'text-ink-soft'}>
                  {r.label}
                  {!r.works && <span className="text-ink-faint">, needs {r.needs}</span>}
                </span>
              </li>
            ))}
          </ul>

          {/* The honest gap between the two shapes of file, said here rather than left to be
              found in an empty chart later. */}
          {!capability.per_order && (
            <Notice tone="info" word="Daily" title="This file has no receipt references" className="text-[13px]">
              It becomes one clearly-marked entry per trading day rather than individual
              bills. Revenue, GP and product mix come out right; average spend and covers
              cannot be worked out from it at all.
            </Notice>
          )}
        </div>
      )}

      {mapping && (
        <>
          <div className="rounded-lg border border-hairline bg-surface" data-testid="sales-import-products">
            <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-hairline px-5 py-3">
              <h2 className="font-heading text-[15px] font-bold text-ink">
                {mapping.mapped} of {mapping.total} products matched
              </h2>
              <span className="text-[12.5px] text-ink-faint">Biggest sellers first. Start at the top and stop when you like</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[13.5px]" style={{ minWidth: '620px' }}>
                <thead>
                  <tr className="text-left font-heading text-[11px] font-bold uppercase tracking-[0.08em] text-ink-faint">
                    <th className="px-5 py-2">Their product</th>
                    <th className="py-2 pr-4 text-right">Sold</th>
                    <th className="py-2 pr-4 text-right">Revenue</th>
                    <th className="py-2 pr-5">On your menu</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((p) => {
                    const suggested = new Set((p.suggestions || []).map((s) => s.id));
                    return (
                    <tr key={p.key} className="border-t border-hairline" data-testid={`sales-product-${p.key}`}>
                      <td className="px-5 py-2">
                        <div className="font-medium text-ink">{p.name || <span className="text-ink-faint">unnamed</span>}</div>
                        {p.code && <div className="font-mono text-[11.5px] text-ink-faint">{p.code}</div>}
                      </td>
                      <td className="py-2 pr-4 text-right font-mono tabular-nums text-ink-soft">{qty(p.quantity)}</td>
                      <td className="py-2 pr-4 text-right font-mono tabular-nums text-ink">{gbp(p.revenue)}</td>
                      <td className="py-2 pr-5">
                        <Select
                          value={p.menu_item_id || NONE}
                          onValueChange={(v) => decide(p.key, v === NONE ? null : v)}
                        >
                          <SelectTrigger className="h-8 w-[250px] text-[13px]" aria-label={`Match ${p.name} to a dish`}>
                            <SelectValue placeholder="Not matched" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={NONE}>Not matched</SelectItem>
                            {(p.suggestions || []).length > 0 && (
                              <SelectGroup>
                                <SelectLabel>Best guesses</SelectLabel>
                                {p.suggestions.map((s) => (
                                  <SelectItem key={`s-${s.id}`} value={s.id}>{s.name}</SelectItem>
                                ))}
                              </SelectGroup>
                            )}
                            <SelectGroup>
                              <SelectLabel>The whole menu</SelectLabel>
                              {/* Minus anything already offered above. A dish listed twice
                                  under one value makes the closed trigger read its name
                                  twice over, because the value renders every item that
                                  carries it. */}
                              {menu.filter((m) => !suggested.has(m.id)).map((m) => (
                                <SelectItem key={m.id} value={m.id}>
                                  {m.name}{m.category ? ` · ${m.category}` : ''}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                        {/* Recognised and confirmed are two different facts. */}
                        <div className="mt-0.5 text-[11.5px] text-ink-faint">
                          {p.remembered
                            ? 'you decided this'
                            : p.matched_on_code ? 'their code matches yours' : ' '}
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {products.length > 50 && !all && (
              <div className="flex flex-wrap items-center gap-3 border-t border-hairline px-5 py-2.5 text-[12.5px] text-ink-faint">
                The 50 biggest sellers of {products.length}. The rest import either way:
                an unmatched line still counts towards revenue.
                <button type="button" onClick={() => setAll(true)}
                        className="font-semibold text-verlo underline-offset-2 hover:underline"
                        data-testid="sales-import-show-all">
                  Show all {products.length}
                </button>
              </div>
            )}

            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-hairline px-5 py-3">
              {done
                ? <Signal tone="ok" data-testid="sales-import-done">
                    {done.orders} entries across {done.days} {done.days === 1 ? 'day' : 'days'}
                    {done.from ? ` · ${day(done.from)} to ${day(done.to)}` : ''}
                    {done.replaced ? ` · ${done.replaced} replaced` : ''}
                  </Signal>
                : <span className="text-[12.5px] text-ink-faint">
                    Nothing is imported until you press the button. Re-importing a corrected
                    export replaces the days it covers rather than counting them twice.
                  </span>}
              <Button onClick={commit} disabled={busy || Boolean(done)}
                      className="bg-verlo hover:bg-verlo-hover" data-testid="sales-import-commit">
                {busy ? 'Importing…' : 'Import these sales'}
              </Button>
            </div>
          </div>

          {done && (done.unmapped_lines > 0 || done.skipped_undated > 0) && (
            <Notice tone="warn" word="Imported" title="Worth a look before you trust the reports" className="text-[13px]">
              {done.unmapped_lines > 0 && (
                <div>
                  {done.unmapped_lines} lines had no matching dish. They count towards revenue
                  at no cost, so GP reads high and the product mix leaves them out until
                  they&rsquo;re matched. Match them above and import again.
                </div>
              )}
              {done.skipped_undated > 0 && (
                <div>{done.skipped_undated} rows had no date we could read and were left out.</div>
              )}
            </Notice>
          )}
        </>
      )}
    </div>
  );
};

export default SalesImportPage;
